import { useMemo, useState } from 'react'
import { ShieldCheck, CalendarCheck, MailCheck, Search, Clock, ArrowRight } from 'lucide-react'
import { useCurrency, useStoreContent, price } from '../shared'
import { t } from '../../../../i18n'

// Clinic / office directory: trust badges, a sidebar to filter by
// department, and a structured table of services - built for scanning
// and comparing, not for mood.
export default function BookingProfessionalTemplate({ services, storeName, onSelectService }) {
  const currency = useCurrency()
  const content = useStoreContent({ storeName, title: 'Book your appointment online.', subtitle: 'Choose a service, pick a free time on the calendar, and get your confirmation by email.' })
  const [query, setQuery] = useState('')
  const [dept, setDept] = useState('all')
  const departments = useMemo(() => {
    const counts = {}
    for (const s of services) { const k = s.category || 'General'; counts[k] = (counts[k] || 0) + 1 }
    return Object.entries(counts)
  }, [services])
  const visible = services.filter(s =>
    (dept === 'all' || (s.category || 'General') === dept) &&
    `${s.name} ${s.description || ''}`.toLowerCase().includes(query.trim().toLowerCase()))

  return (
    <div className="pb-16" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="grid sm:grid-cols-3 gap-3 mb-8">
        {[[ShieldCheck, 'Qualified specialists'], [CalendarCheck, 'Book online 24/7'], [MailCheck, 'Email confirmation']].map(([Icon, label]) => (
          <div key={label} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
            <span className="w-9 h-9 rounded-lg bg-[#1e3a5f]/10 text-[color:var(--brand,#1e3a5f)] grid place-items-center"><Icon size={18} /></span>
            <span className="text-sm font-semibold text-slate-800">{t(label)}</span>
          </div>
        ))}
      </div>

      <section className="rounded-2xl bg-[color:var(--brand,#1e3a5f)] text-white p-8 md:p-12 mb-10 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-sky-200">{content.name}</p>
          <h1 className="text-3xl md:text-5xl font-bold mt-3 leading-tight">{content.heroTitle}</h1>
          <p className="text-slate-200 mt-4 max-w-xl">{content.heroSubtitle}</p>
        </div>
        {content.heroImage
          ? <img src={content.heroImage} alt="" className="w-full h-52 object-cover rounded-xl" />
          : (
            <ol className="bg-white/10 rounded-xl p-6 space-y-4 text-sm">
              {['Choose a service', 'Pick a green day on the calendar', 'Receive your confirmation'].map((step, i) => (
                <li key={step} className="flex items-center gap-3"><span className="w-7 h-7 rounded-full bg-white text-[color:var(--brand,#1e3a5f)] font-bold grid place-items-center">{i + 1}</span>{step}</li>
              ))}
            </ol>
          )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-8 items-start">
        <aside className="min-w-0 bg-white border border-slate-200 rounded-2xl p-5 lg:sticky lg:top-24">
          <label className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 mb-5">
            <Search size={16} className="text-slate-400" />
            <input aria-label={t("Search services")} value={query} onChange={e => setQuery(e.target.value)} placeholder={t("Search services")} className="outline-none text-sm w-full" />
          </label>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">{t("Departments")}</p>
          <ul className="space-y-1">
            {[['all', services.length], ...departments].map(([name, count]) => (
              <li key={name}>
                <button onClick={() => setDept(name)} aria-pressed={dept === name}
                  className={`w-full flex justify-between px-3 py-2 rounded-lg text-sm ${dept === name ? 'bg-[color:var(--brand,#1e3a5f)] text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'}`}>
                  <span>{name === 'all' ? t("All services") : name}</span><span className="opacity-70">{count}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="min-w-0 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[1fr_110px_120px_130px] gap-4 px-6 py-3 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <span>{t("Service")}</span><span>{t("Duration")}</span><span>{t("Fee")}</span><span />
          </div>
          {visible.length === 0 ? (
            <p className="p-10 text-center text-slate-500">{t("No services match your search.")}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {visible.map(s => (
                <li key={s.id} className="grid md:grid-cols-[1fr_110px_120px_130px] gap-3 md:gap-4 px-6 py-5 items-center hover:bg-slate-50/70">
                  <div>
                    <p className="font-semibold text-slate-900">{s.name}</p>
                    {s.description && <p className="text-sm text-slate-500 mt-0.5">{t(s.description)}</p>}
                    <p className="text-[11px] text-[color:var(--brand,#1e3a5f)] font-semibold uppercase tracking-wider mt-1">{s.category || t("General")}</p>
                  </div>
                  <span className="text-sm text-slate-600 flex items-center gap-1.5"><Clock size={14} />{s.duration_minutes} {t("min")}</span>
                  <span className="text-sm font-semibold text-slate-900">{price(s.price, currency)}</span>
                  <button onClick={() => onSelectService(s)} className="flex items-center justify-center gap-2 bg-[color:var(--brand,#1e3a5f)] hover:bg-[color:var(--brand,#16304f)] text-white text-sm font-semibold rounded-lg px-4 py-2.5">
                    {t("Schedule")} <ArrowRight size={15} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
